import {
  Component,
  inject,
  TemplateRef,
  viewChild,
  signal,
  AfterViewInit,
  computed
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';

import {
  DataDisplayComponent,
  ButtonComponent,
  DataContainerComponent,
  TableComponent,
  ColumnConfig,
  ColumnType,
  FilterOption,
  BadgeComponent,
  ConfirmDialogWithReasonComponent,
  ConfirmDialogWithReasonData,
  ConfirmDialogWithReasonResult
} from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable, switchMap, catchError, of, take, Subject, startWith } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OrganizationMemberListItem,
  OrganizationDetails,
  ActivityState,
  AdmissionState
} from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { AuthService } from '../../../../../services/auth/auth.service';
import { UserRole } from '../../../../../services/auth/auth.role';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '../../../../organisms/mock-data-switcher/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-organization-detail-page',
  imports: [
    AsyncPipe,
    CommonModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    TableComponent,
    ButtonComponent,
    BadgeComponent,
    ProtectedContentComponent,
    ConfirmDialogComponent,
    ConfirmDialogWithReasonComponent
  ],
  templateUrl: './organization-detail-page.component.html',
  styleUrl: './organization-detail-page.component.scss'
})
export class OrganizationDetailPageComponent implements AfterViewInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  requiredRoles = this.navigationService.getAccessRoles('admin/organizations');

  /** Whether the user's organization is pending (blocks edit/add actions) */
  isOrganizationPending = signal(false);

  /** Whether the current user is a Dataspace Authority */
  isDataspaceAuthority = this.authService.userRoles$.value.includes(UserRole.DataspaceAuthority);

  /** Confirmation dialog state */
  confirmDialogData = signal<ConfirmDialogData | null>(null);
  reasonDialogData = signal<ConfirmDialogWithReasonData | null>(null);
  private pendingAction = signal<'suspend' | 'reactivate' | 'delete' | null>(null);

  /** Current organization data for action checks */
  currentOrganization = signal<OrganizationDetails | null>(null);

  // Computed: hide back button if user is only OrganizationAdmin
  showBackButton = computed(() => {
    const userRoles = this.authService.userRoles$.value;
    const isOnlyOrgAdmin =
      userRoles.includes(UserRole.OrganizationAdmin) &&
      !userRoles.includes(UserRole.DataspaceAuthority);
    return !isOnlyOrgAdmin;
  });

  /** Whether the current user can edit organizations (role check + org not pending) */
  canEditOrganizations = this.navigationService
    .getAccessRoles('admin/organizations/edit')
    .some((role) => this.authService.userRoles$.value.includes(role));

  /** Whether the current user can invite new members (role check + org not pending) */
  canAddMembers = this.navigationService
    .getAccessRoles('admin/organizations/members/add')
    .some((role) => this.authService.userRoles$.value.includes(role));

  actionsTemplateRef = viewChild<TemplateRef<OrganizationMemberListItem>>('columnActions');
  rolesTemplateRef = viewChild<TemplateRef<OrganizationMemberListItem>>('columnRoles');
  private actionsTemplate = signal<TemplateRef<OrganizationMemberListItem> | undefined>(undefined);
  private rolesTemplate = signal<TemplateRef<OrganizationMemberListItem> | undefined>(undefined);
  private templatesReady = signal(false);

  constructor() {
    this.organizationService.isOrganizationPending$().subscribe((pending) => {
      this.isOrganizationPending.set(pending);
    });
  }

  approvalForm = this.fb.group({
    reasoning: ['', Validators.required]
  });

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  private refreshTrigger$ = new Subject<void>();

  data$: Observable<OrganizationDetails> = this.id$.pipe(
    switchMap((id) =>
      this.refreshTrigger$.pipe(
        startWith(undefined),
        switchMap(() => this.organizationService.getDetailsById(id))
      )
    ),
    map((org) => {
      this.currentOrganization.set(org);
      return org;
    }),
    catchError(() => {
      this.notificationService.showError('organization.detail.loadingError', 5000);
      return of();
    })
  );

  organisationDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('organization.control.bpnl.label'),
          value: [dto.bpnl]
        },
        {
          label: 'DID',
          value: [dto.did || '-']
        },
        {
          label: this.translocoService.translate('organization.control.activityState.label'),
          value: [
            this.translocoService.translate(
              'organization.control.activityState.options.' + dto.activityState
            ) ||
              dto.activityState ||
              '-'
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approval.summary.foundingDate.label'),
          value: [
            dto.foundingDate
              ? new Date(dto.foundingDate).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              : ''
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.address.label'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.commercialRegisterNumber.label'),
          value: [dto.commercialRegisterNumber, dto.commercialRegisterCourt]
        },
        {
          label: this.translocoService.translate('approval.summary.website.label'),
          value: [dto.website]
        },
        {
          label: this.translocoService.translate('approval.summary.taxIdNumber.label'),
          value: [dto.taxIdNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.sector.label'),
          value: [dto.sector]
        },
        {
          label: this.translocoService.translate('approval.summary.description.label'),
          value: [dto.description]
        }
      ];
    })
  );

  member$: Observable<OrganizationMemberListItem[]> = this.id$.pipe(
    switchMap((id) => this.organizationService.findMembersOfOrganization(id))
  );

  columnConfig$: Observable<ColumnConfig<OrganizationMemberListItem>[]> = this.translocoService
    .selectTranslate([])
    .pipe(
      switchMap(() => {
        return new Observable<ColumnConfig<OrganizationMemberListItem>[]>((observer) => {
          const checkTemplates = () => {
            if (this.templatesReady()) {
              observer.next(this.buildColumnConfig());
              observer.complete();
            } else {
              setTimeout(checkTemplates, 50);
            }
          };
          checkTemplates();
        });
      })
    );

  private buildColumnConfig(): ColumnConfig<OrganizationMemberListItem>[] {
    const actionsTmpl = this.actionsTemplate();
    const rolesTmpl = this.rolesTemplate();
    return [
      {
        name: 'firstName',
        headerLabel: this.translocoService.translate(
          'organization.members.control.firstName.label'
        ),
        columnType: ColumnType.PLAIN
      },
      {
        name: 'lastName',
        headerLabel: this.translocoService.translate('organization.members.control.lastName.label'),
        columnType: ColumnType.PLAIN,
        filterable: true,
        sortable: true
      },
      {
        name: 'position',
        headerLabel: this.translocoService.translate('organization.members.control.position.label'),
        columnType: ColumnType.PLAIN,
        filterable: true,
        sortable: true
      },
      {
        name: 'roles',
        headerLabel: this.translocoService.translate('organization.members.control.roles.label'),
        columnType: ColumnType.TEMPLATE,
        template: rolesTmpl as any,
        filterable: true,
        filterOptions: [
          {
            value: 'ORGANIZATION_USER',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_user'
            )
          },
          {
            value: 'ORGANIZATION_REPRESENTATIVE',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_representative'
            )
          },
          {
            value: 'ORGANIZATION_ADMIN',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_admin'
            )
          },
          {
            value: 'DATASPACE_AUTHORITY',
            label: this.translocoService.translate(
              'organization.member.roles.labels.dataspace_authority'
            )
          }
        ] as FilterOption[]
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.action.label'),
        template: actionsTmpl as any
      }
    ];
  }

  backToList() {
    void this.router.navigate(['admin', 'organizations']);
  }

  ngAfterViewInit(): void {
    this.actionsTemplate.set(this.actionsTemplateRef());
    this.rolesTemplate.set(this.rolesTemplateRef());
    this.templatesReady.set(true);
  }

  edit() {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'edit']);
    });
  }

  viewMember(member: OrganizationMemberListItem) {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'members', member.id]);
    });
  }

  editMember(member: OrganizationMemberListItem) {
    this.id$.subscribe((id) => {
      void this.router.navigate([
        'admin',
        'organizations',
        'details',
        id,
        'members',
        member.id,
        'edit'
      ]);
    });
  }

  addMember() {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'members', 'add']);
    });
  }

  viewApprovalStatus() {
    void this.router.navigate(['organization', 'approval-status']);
  }

  getTyped(row: unknown) {
    return row as OrganizationMemberListItem;
  }

  // --- Organization lifecycle actions (Dataspace Authority only) ---

  /** Whether the current organization can be suspended (APPROVED + ACTIVE) */
  canSuspend = computed(() => {
    const org = this.currentOrganization();
    return (
      org &&
      org.admissionState === AdmissionState.APPROVED &&
      org.activityState === ActivityState.ACTIVE
    );
  });

  /** Whether the current organization can be reactivated (APPROVED + INACTIVE) */
  canReactivate = computed(() => {
    const org = this.currentOrganization();
    return (
      org &&
      org.admissionState === AdmissionState.APPROVED &&
      org.activityState === ActivityState.INACTIVE
    );
  });

  requestSuspend(): void {
    this.pendingAction.set('suspend');
    this.reasonDialogData.set({
      title: this.translocoService.translate('organization.detail.actions.suspendConfirm.title'),
      message: this.translocoService.translate(
        'organization.detail.actions.suspendConfirm.message'
      ),
      reasonLabel: this.translocoService.translate(
        'organization.detail.actions.suspendConfirm.reasonLabel'
      ),
      confirmText: this.translocoService.translate(
        'organization.detail.actions.suspendConfirm.confirm'
      ),
      cancelText: this.translocoService.translate(
        'organization.detail.actions.suspendConfirm.cancel'
      )
    });
  }

  requestReactivate(): void {
    this.pendingAction.set('reactivate');
    this.confirmDialogData.set({
      title: this.translocoService.translate('organization.detail.actions.reactivateConfirm.title'),
      message: this.translocoService.translate(
        'organization.detail.actions.reactivateConfirm.message'
      ),
      confirmText: this.translocoService.translate(
        'organization.detail.actions.reactivateConfirm.confirm'
      ),
      cancelText: this.translocoService.translate(
        'organization.detail.actions.reactivateConfirm.cancel'
      )
    });
  }

  requestDelete(): void {
    this.pendingAction.set('delete');
    this.reasonDialogData.set({
      title: this.translocoService.translate('organization.detail.actions.deleteConfirm.title'),
      message: this.translocoService.translate('organization.detail.actions.deleteConfirm.message'),
      reasonLabel: this.translocoService.translate(
        'organization.detail.actions.deleteConfirm.reasonLabel'
      ),
      confirmText: this.translocoService.translate(
        'organization.detail.actions.deleteConfirm.confirm'
      ),
      cancelText: this.translocoService.translate(
        'organization.detail.actions.deleteConfirm.cancel'
      )
    });
  }

  onConfirmDialogResult(confirmed: boolean): void {
    const action = this.pendingAction();
    this.confirmDialogData.set(null);
    this.pendingAction.set(null);

    if (!confirmed || !action) return;

    this.id$.pipe(take(1)).subscribe((id) => {
      switch (action) {
        case 'reactivate':
          this.organizationService.reactivateOrganization(id).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                'organization.detail.actions.reactivateSuccess',
                5000
              );
              this.reloadData();
            },
            error: () =>
              this.notificationService.showError(
                'organization.detail.actions.reactivateError',
                5000
              )
          });
          break;
      }
    });
  }

  onReasonDialogResult(result: ConfirmDialogWithReasonResult): void {
    const action = this.pendingAction();
    this.reasonDialogData.set(null);
    this.pendingAction.set(null);

    if (!result.confirmed || !action) return;

    this.id$.pipe(take(1)).subscribe((id) => {
      switch (action) {
        case 'suspend':
          this.organizationService.suspendOrganization(id, result.reason).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                'organization.detail.actions.suspendSuccess',
                5000
              );
              this.reloadData();
            },
            error: () =>
              this.notificationService.showError('organization.detail.actions.suspendError', 5000)
          });
          break;
        case 'delete':
          this.organizationService.deleteOrganization(id, result.reason).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                'organization.detail.actions.deleteSuccess',
                5000
              );
              void this.router.navigate(['admin', 'organizations']);
            },
            error: () =>
              this.notificationService.showError('organization.detail.actions.deleteError', 5000)
          });
          break;
      }
    });
  }

  private reloadData(): void {
    this.refreshTrigger$.next();
  }
}
